import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ListSubheader,
  TextField,
} from '@mui/material';
import Button from '../../../components/Button';
import * as Styled from '../LoyaltyPageReport.styles';
import * as actions from '../../LoyaltyPage/actions';
import ConfirmationDeleteModal from './ConfirmationDeleteModal';

interface Source {
  code: string;
  name: string;
  network: string;
}

interface SkuBonusManagerProps {
  selectedSources: string[];
  allSourcesLabel: string;
  sources: Source[];
  onChangeSources: (event: SelectChangeEvent<string[]>) => void;
  networkCode?: string;
  onSubmitBonus?: (payload: {
    sku: string;
    percent: number;
    network_code?: string;
    source_codes: string[];
  }) => void;
  rows: {
    sku: string;
    pharmacy: string;
    source_code: string;
    mapping_code: string;
    bonus: string;
    author: string;
  }[];
}

type SkuBonusRow = SkuBonusManagerProps['rows'][number];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const NETWORK_PREFIX = '__network__';

const renderChipLabel = (
  value: string,
  allSourcesLabel: string,
  sources: Source[]
) => {
  if (value === allSourcesLabel) return value;

  if (value.startsWith(NETWORK_PREFIX)) {
    const networkKey = value.slice(NETWORK_PREFIX.length);
    return `Сеть: ${networkKey}`;
  }

  return sources.find((source) => source.code === value)?.name || value;
};

const SkuBonusManager = ({
  selectedSources,
  allSourcesLabel,
  sources,
  onChangeSources,
  networkCode,
  onSubmitBonus = () => {},
  rows,
}: SkuBonusManagerProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  // В этом состоянии храним конкретный товар, который пользователь хочет удалить.

  const [rowToDelete, setRowToDelete] = useState<SkuBonusRow | null>(null);

  const [skuValue, setSkuValue] = useState('');
  const [bonusValue, setBonusValue] = useState('');
  const [formErrors, setFormErrors] = useState<{
    sku?: string;
    bonus?: string;
    sources?: string;
  }>({});
  const [sourcesSearch, setSourcesSearch] = useState('');

  // Группировка аптек по network
  const networkGroups = useMemo(
    () =>
      sources.reduce<Map<string, Source[]>>((map, s) => {
        const key = s.network || '__no_network__';
        const group = map.get(key) || [];
        group.push(s);
        map.set(key, group);
        return map;
      }, new Map()),
    [sources]
  );

  const networkKeys = useMemo(
    () => Array.from(networkGroups.keys()),
    [networkGroups]
  );

  // Фильтрация по поиску
  const filteredNetworkGroups = useMemo(() => {
    const q = sourcesSearch.trim().toLowerCase();

    if (!q) return networkGroups;

    return Array.from(networkGroups.entries()).reduce<Map<string, Source[]>>(
      (result, [network, items]) => {
        const networkMatches = network.toLowerCase().includes(q);
        const filtered = networkMatches
          ? items
          : items.filter((s) => s.name.toLowerCase().includes(q));

        if (filtered.length > 0) {
          result.set(network, filtered);
        }
        return result;
      },
      new Map()
    );
  }, [networkGroups, sourcesSearch]);

  // Множество выбранных source codes (без network-меток)
  const selectedCodesSet = useMemo(
    () => new Set(selectedSources.filter((v) => !v.startsWith(NETWORK_PREFIX))),
    [selectedSources]
  );

  const isNetworkFullySelected = (network: string): boolean => {
    const items = networkGroups.get(network);
    if (!items || items.length === 0) return false;
    return items.every((s) => selectedCodesSet.has(s.code));
  };

  // Значения для отображения чипов
  const displayValues = useMemo(() => {
    if (selectedSources.includes(allSourcesLabel)) {
      return [allSourcesLabel];
    }

    const usedCodes = new Set<string>();

    const networkChips = networkKeys
      .filter((network) => isNetworkFullySelected(network))
      .map((network) => {
        (networkGroups.get(network) || []).forEach((s) =>
          usedCodes.add(s.code)
        );
        return `${NETWORK_PREFIX}${network}`;
      });

    const remainingChips = Array.from(selectedCodesSet).filter(
      (code) => !usedCodes.has(code)
    );

    return [...networkChips, ...remainingChips];
  }, [
    selectedSources,
    selectedCodesSet,
    networkKeys,
    networkGroups,
    allSourcesLabel,
  ]);

  // Кастомный обработчик onChange для Select
  const handleSelectChange = (event: SelectChangeEvent<string[]>) => {
    const rawValue = event.target.value as string[];

    const hadAll = selectedSources.includes(allSourcesLabel);
    const hasAll = rawValue.includes(allSourcesLabel);

    if (hasAll && !hadAll) {
      const syntheticEvent = {
        ...event,
        target: { ...event.target, value: [] },
      } as SelectChangeEvent<string[]>;
      onChangeSources(syntheticEvent);
      return;
    }

    if (hadAll && !hasAll) {
      const syntheticEvent = {
        ...event,
        target: { ...event.target, value: [] },
      } as SelectChangeEvent<string[]>;
      onChangeSources(syntheticEvent);
      return;
    }

    const prevCodes = new Set(
      selectedSources.filter(
        (v) => !v.startsWith(NETWORK_PREFIX) && v !== allSourcesLabel
      )
    );

    const newSelected = new Set(prevCodes);

    // Обработка кликов по network-меткам
    rawValue
      .filter((val) => val.startsWith(NETWORK_PREFIX))
      .forEach((val) => {
        const network = val.slice(NETWORK_PREFIX.length);
        const items = networkGroups.get(network) || [];
        const codes = items.map((s) => s.code);
        const allSelected = codes.every((c) => newSelected.has(c));

        if (allSelected) {
          codes.forEach((c) => newSelected.delete(c));
        } else {
          codes.forEach((c) => newSelected.add(c));
        }
      });

    // Обработка кликов по обычным аптекам — добавление новых
    rawValue
      .filter(
        (val) => !val.startsWith(NETWORK_PREFIX) && val !== allSourcesLabel
      )
      .forEach((val) => {
        if (!prevCodes.has(val)) {
          newSelected.add(val);
        }
      });

    // Обработка снятия выделения обычных аптек
    const networkClickedCodes = new Set(
      rawValue
        .filter((val) => val.startsWith(NETWORK_PREFIX))
        .flatMap((val) => {
          const network = val.slice(NETWORK_PREFIX.length);
          return (networkGroups.get(network) || []).map((s) => s.code);
        })
    );

    Array.from(prevCodes)
      .filter(
        (code) => !rawValue.includes(code) && !networkClickedCodes.has(code)
      )
      .forEach((code) => newSelected.delete(code));

    const finalValue = Array.from(newSelected);
    const syntheticEvent = {
      ...event,
      target: { ...event.target, value: finalValue },
    } as SelectChangeEvent<string[]>;
    onChangeSources(syntheticEvent);
  };

  // Валидация концентрируется в одной функции для переиспользования.
  const validateForm = () => {
    const errors: typeof formErrors = {};
    if (!skuValue.trim()) errors.sku = 'Укажите SKU товара';
    if (!bonusValue.trim()) {
      errors.bonus = 'Укажите бонус';
    } else {
      const numericBonus = Number(bonusValue);
      if (Number.isNaN(numericBonus)) errors.bonus = 'Некорректное число';
      if (numericBonus <= 0 || numericBonus > 100)
        errors.bonus = 'Бонус от 0 до 100';
    }
    if (!selectedSources.length)
      errors.sources = 'Выберите хотя бы одну аптеку';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Позволяем переиспользовать сабмит как click, так и onSubmit.
  const handleSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!validateForm()) return;

    const isAllSourcesSelected =
      selectedSources.length === 1 && selectedSources[0] === allSourcesLabel;
    const sourceCodesPayload = isAllSourcesSelected ? [] : selectedSources;
    const networkCodePayload = isAllSourcesSelected ? networkCode : undefined;

    onSubmitBonus({
      sku: skuValue.trim(),
      percent: Number(bonusValue),
      network_code: networkCodePayload,
      source_codes: [...sourceCodesPayload],
    });

    setSkuValue('');
    setBonusValue('');
    setFormErrors({});
  };

  const handleDeleteProduct = (
    sku: string,
    sourceCode: string,
    mappingCode: string
  ) => {
    dispatch(
      actions.deleteLoyaltyProduct.request({
        sku,
        source_code: sourceCode,
        mapping_code: mappingCode,
      })
    );
  };

  const getNetworkHeaderBg = (
    allSelected: boolean,
    someSelected: boolean
  ): string => {
    if (allSelected) return 'action.selected';
    if (someSelected) return 'action.hover';
    return 'transparent';
  };

  return (
    <>
      <Styled.ButtonsBlock as="form" onSubmit={handleSubmit} noValidate>
        <FormControl error={Boolean(formErrors.sku)} variant="standard">
          <Styled.AdditionalBonusField>
            <span className="label">
              {t('LoyaltyPageReport.ADD_PRODUCT_BY_SKU')}{' '}
            </span>
            <TextField
              size="small"
              variant="outlined"
              value={skuValue}
              onChange={(event) => setSkuValue(event.target.value)}
              error={!!formErrors.sku}
              helperText={formErrors.sku}
              placeholder="SKU"
            />
          </Styled.AdditionalBonusField>
        </FormControl>

        <FormControl
          sx={{ minWidth: '100px' }}
          error={Boolean(formErrors.bonus)}
          variant="standard"
        >
          <Styled.AdditionalBonusField>
            <span className="label">
              {`${t('LoyaltyPageReport.BONUSES')}`}{' '}
            </span>
            <TextField
              size="small"
              variant="outlined"
              value={bonusValue}
              onChange={(event) => setBonusValue(event.target.value)}
              error={!!formErrors.bonus}
              helperText={formErrors.bonus}
              placeholder="%"
            />
          </Styled.AdditionalBonusField>
        </FormControl>

        <FormControl
          sx={{ m: 1, width: 400 }}
          error={Boolean(formErrors.sources)}
        >
          <InputLabel id="loyalty-report-sources-label">
            {t('LoyaltyPageReport.PHARMACIES')}
          </InputLabel>
          <Select<string[]>
            labelId="loyalty-report-sources-label"
            id="loyalty-report-sources"
            multiple
            value={selectedSources}
            onChange={handleSelectChange}
            input={
              <OutlinedInput id="loyalty-report-sources-input" label="Аптеки" />
            }
            renderValue={() => {
              const chips = displayValues.slice(0, 3);
              const rest = displayValues.length - 3;
              return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {chips.map((value) => (
                    <Chip
                      key={value}
                      label={renderChipLabel(value, allSourcesLabel, sources)}
                    />
                  ))}
                  {rest > 0 && <Chip label={`+${rest}`} />}
                </Box>
              );
            }}
            MenuProps={MENU_PROPS}
            onClose={() => setSourcesSearch('')}
          >
            <ListSubheader
              sx={{ bgcolor: 'background.paper', marginTop: '10px' }}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
            >
              <TextField
                autoFocus
                size="small"
                placeholder="Поиск по аптеке"
                fullWidth
                value={sourcesSearch}
                onChange={(e) => setSourcesSearch(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                }}
              />
            </ListSubheader>

            <MenuItem key={allSourcesLabel} value={allSourcesLabel}>
              {allSourcesLabel}
            </MenuItem>

            {Array.from(filteredNetworkGroups.entries()).flatMap(
              ([network, items]) => {
                const allNetworkCodes = (networkGroups.get(network) || []).map(
                  (s) => s.code
                );
                const allSelected = allNetworkCodes.every((c) =>
                  selectedCodesSet.has(c)
                );
                const someSelected = allNetworkCodes.some((c) =>
                  selectedCodesSet.has(c)
                );

                return [
                  <MenuItem
                    key={`${NETWORK_PREFIX}${network}`}
                    value={`${NETWORK_PREFIX}${network}`}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: getNetworkHeaderBg(
                        allSelected,
                        someSelected
                      ),
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      mt: 0.5,
                    }}
                  >
                    Сеть: {network}
                  </MenuItem>,
                  ...items.map((source) => {
                    const isSelected = selectedCodesSet.has(source.code);
                    return (
                      <MenuItem
                        key={source.code}
                        value={source.code}
                        sx={{
                          pl: 4,
                          backgroundColor: isSelected
                            ? 'action.selected'
                            : 'transparent',
                          color: isSelected ? 'text.secondary' : 'text.primary',
                        }}
                      >
                        {source.name}
                      </MenuItem>
                    );
                  }),
                ];
              }
            )}
          </Select>
          {formErrors.sources && (
            <FormHelperText>{formErrors.sources}</FormHelperText>
          )}
        </FormControl>
        <Button text="Добавить" variant="contained" onClick={handleSubmit} />
      </Styled.ButtonsBlock>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t('LoyaltyPageReport.ProductsTable.SKU')}</TableCell>
            <TableCell>
              {t('LoyaltyPageReport.ProductsTable.PHARMACY')}
            </TableCell>
            <TableCell>{t('LoyaltyPageReport.ProductsTable.BONUS')}</TableCell>
            <TableCell>{t('LoyaltyPageReport.ProductsTable.AUTHOR')}</TableCell>
            <TableCell>{t('LoyaltyPageReport.ProductsTable.ACTION')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.sku}-${row.pharmacy}`}>
              <TableCell>{row.sku}</TableCell>
              <TableCell>{row.pharmacy}</TableCell>
              <TableCell>{row.bonus}</TableCell>
              <TableCell>{row.author}</TableCell>
              <TableCell>
                <Styled.DeleteProductButton
                  // При клике запоминаем выбранную строку и открываем модалку.
                  onClick={() => {
                    setRowToDelete(row);
                    setConfirmDeleteModalOpen(true);
                  }}
                >
                  x
                </Styled.DeleteProductButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {/* Модалка рендерится один раз и работает с выбранной строкой из состояния. */}
      {rowToDelete && (
        <ConfirmationDeleteModal
          open={confirmDeleteModalOpen}
          onClose={() => {
            setConfirmDeleteModalOpen(false);
            setRowToDelete(null);
          }}
          onDelete={() => {
            handleDeleteProduct(
              rowToDelete.sku,
              rowToDelete.source_code,
              rowToDelete.mapping_code
            );
            setConfirmDeleteModalOpen(false);
            setRowToDelete(null);
          }}
          sku={rowToDelete.sku}
        />
      )}
    </>
  );
};

export default SkuBonusManager;
